import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, fmtDate } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageSquare, Send, Loader2, Globe, Phone, Mail, MessageCircle, Check } from "lucide-react";
import { toast } from "sonner";

const CH_ICON = { Website: Globe, Chat: MessageCircle, Phone, SMS: MessageSquare, Email: Mail };
const STATUSES = ["NEW", "OPEN", "WAITING", "FOLLOW_UP", "RESOLVED"];

export default function Inbox() {
  const { hasPerm } = useAuth();
  const canManage = hasPerm("crm:manage");
  const [rows, setRows] = useState(null);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [filter, setFilter] = useState("ALL");

  const load = useCallback(async () => {
    try { const { data } = await api.get("/conversations"); setRows(data); if (active) setActive(data.find((c) => c.id === active.id) || null); }
    catch (e) { toast.error(apiError(e)); setRows([]); }
  }, [active]);
  useEffect(() => { load(); }, []); // eslint-disable-line

  const send = async () => {
    if (!reply.trim()) return;
    try { await api.post(`/conversations/${active.id}/reply`, { text: reply }); setReply(""); toast.success("Reply sent (DEMO)"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const setStatus = async (v) => { try { await api.put(`/conversations/${active.id}`, { status: v }); toast.success("Updated"); load(); } catch (e) { toast.error(apiError(e)); } };
  const createInquiry = async () => {
    try { await api.post("/inquiries", { name: active.contact, message: active.last_message, service: "General", status: "NEW", priority: "MEDIUM", source: active.channel }); toast.success("Inquiry created in CRM"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const list = (rows || []).filter((c) => filter === "ALL" || c.status === filter);

  return (
    <div data-testid="inbox-page">
      <PageHeader title="Unified Inbox" subtitle="Centralized communication across website, chat, phone, SMS and email."
        actions={<span className="inline-flex items-center gap-1.5 rounded-full bg-ochre/15 text-ochre text-xs font-bold px-3 py-1.5">DEMO COMMUNICATIONS</span>} />

      <div className="flex gap-2 mb-4 flex-wrap">
        {["ALL", ...STATUSES].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`inbox-filter-${s}`}
            className={`rounded-full px-3.5 py-1.5 text-sm font-medium ${filter === s ? "bg-primary text-primary-foreground" : "bg-secondary hover:bg-accent"}`}>{s.replace("_", " ")}</button>
        ))}
      </div>

      <div className="grid lg:grid-cols-5 gap-5">
        <div className="lg:col-span-2 border border-border rounded-lg bg-card divide-y divide-border max-h-[620px] overflow-y-auto">
          {rows === null ? <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
            : list.length === 0 ? <div className="p-8 text-center text-muted-foreground text-sm">No conversations.</div>
            : list.map((c) => {
              const Ic = CH_ICON[c.channel] || MessageSquare;
              return (
                <button key={c.id} onClick={() => setActive(c)} data-testid="inbox-conversation"
                  className={`w-full text-left p-4 hover:bg-accent transition-colors ${active?.id === c.id ? "bg-accent" : ""}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-sm flex items-center gap-1.5"><Ic className="w-3.5 h-3.5 text-primary" /> {c.contact}</span>
                    <StatusBadge value={c.status} />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 truncate">{c.last_message}</p>
                  <p className="text-[11px] text-muted-foreground mt-1">{c.channel} · {fmtDate(c.updated_at)}</p>
                </button>
              );
            })}
        </div>

        <div className="lg:col-span-3 border border-border rounded-lg bg-card min-h-[400px] flex flex-col">
          {!active ? <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm"><MessageSquare className="w-5 h-5 mr-2" /> Select a conversation</div> : (
            <>
              <div className="p-4 border-b border-border flex items-center justify-between gap-3 flex-wrap">
                <div><p className="font-heading font-semibold">{active.contact}</p><p className="text-xs text-muted-foreground">{active.channel}</p></div>
                <div className="flex items-center gap-2">
                  {canManage && <Select value={active.status} onValueChange={setStatus}><SelectTrigger className="w-[140px] h-9" data-testid="inbox-status"><SelectValue /></SelectTrigger><SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent></Select>}
                  {canManage && <Button size="sm" variant="outline" onClick={createInquiry} data-testid="inbox-create-inquiry">Create Inquiry</Button>}
                </div>
              </div>
              <div className="flex-1 p-4 space-y-3 overflow-y-auto max-h-[380px]">
                {(active.messages || []).map((m) => (
                  <div key={m.id} className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${m.from === "staff" ? "ml-auto bg-primary text-primary-foreground" : "bg-secondary"}`}>
                    <p>{m.text}</p>
                    <p className={`text-[10px] mt-1 ${m.from === "staff" ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{m.by} · {fmtDate(m.at)}</p>
                  </div>
                ))}
              </div>
              {canManage && (
                <div className="p-3 border-t border-border flex gap-2">
                  <Input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Type a reply… (DEMO)" onKeyDown={(e) => e.key === "Enter" && send()} data-testid="inbox-reply-input" />
                  <Button onClick={send} data-testid="inbox-send"><Send className="w-4 h-4" /></Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
